import { type Options, type PointOptionsObject, type SeriesAreaOptions } from 'highcharts';
import { useEffect } from 'react';

import { COLOR_GRAY_100, COLOR_GRAY_700 } from '@/styles/palette';

import { useFlareContext } from '../FlareContext';
import AreaLoader from '../loaders/AreaLoader';
import {
  type FlareChart,
  type FlareDataPointOptions,
  type FlareOptionsPositionValue,
  type FlareSeriesOptions,
  type SanitizedFlareProps,
} from '../types';
import { getSelectionLine } from '../utils/mark-util';
import { getStackingFromPosition } from '../utils/position-utils';

type AreaProps = {
  data?: FlareSeriesOptions[];
  colors?: string[];
  x?: string;
  y?: string;
  fillOpacity?: number;
  position: FlareOptionsPositionValue;
};

const buildAreaOptions = (
  options: Options,
  parentProps: SanitizedFlareProps,
  props: AreaProps,
): Options => {
  const { data: chartData, colors: chartColors, parseX, parseY } = parentProps;
  const {
    data: markData,
    x = 'x',
    y = 'y',
    fillOpacity = 0.25,
    position = 'overlap',
    colors: markColors,
  } = props;
  const data = markData ?? chartData;
  const colors = markColors ?? chartColors;

  const series: SeriesAreaOptions[] | undefined = data?.map(
    (series: FlareSeriesOptions, seriesIndex: number) => {
      const { name, data: seriesData, ...rest } = series;

      const newSeriesData = seriesData.map((item: FlareDataPointOptions) => {
        const newItem: PointOptionsObject = {
          ...item,
          x: parseX(item[x]),
          y: parseY(item[y]),
          colorIndex: seriesIndex,
        };
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete newItem[x as keyof PointOptionsObject];
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete newItem[y as keyof PointOptionsObject];

        return newItem;
      });

      return {
        type: 'area',
        ...rest,
        name,
        color: colors[seriesIndex],
        lineColor: colors[seriesIndex],
        fillOpacity,
        data: newSeriesData,
        states: {
          inactive: {
            opacity: 1,
          },
        },
        marker: {
          enabled: false,
          radius: 0.1,
          fillOpacity: 1,
          fillColor: colors[seriesIndex],
          states: {
            hover: {
              enabled: true,
              fillColor: undefined,
              lineColor: COLOR_GRAY_100,
              lineWidth: 1,
              radius: 5,
              animation: {
                duration: 200,
              },
            },
            select: {
              enabled: true,
              fillColor: undefined,
              lineColor: COLOR_GRAY_100,
              lineWidth: 1,
              radius: 8,
              animation: {
                duration: 200,
              },
            },
            normal: {
              animation: {
                duration: 200,
              },
            },
          },
        },
      };
    },
  );

  return {
    ...options,
    chart: {
      ...options.chart,
      type: 'area',
    },
    series: options.series ? options.series.concat(series ?? []) : series,
    plotOptions: {
      ...options.plotOptions,
      series: {
        ...options.plotOptions?.series,
        point: {
          ...options.plotOptions?.series?.point,
          events: {
            ...options.plotOptions?.series?.point?.events,
            select: function () {
              setTimeout(() => {
                const chart = this.series.chart as FlareChart;

                if (
                  chart.getSelectedPoints().length &&
                  chart.selectedBackground?.xValue !== this.x
                ) {
                  const line = chart.renderer
                    .path(getSelectionLine(chart))
                    .addClass('stroke-2 [stroke-dasharray:8_6]')
                    .attr({
                      stroke: COLOR_GRAY_700,
                    })
                    .add();

                  chart.selectedBackground = {
                    xValue: this.x,
                    graphic: line,
                  };
                }
              }, 0);
            },
            unselect: function () {
              const chart = this.series.chart as FlareChart;

              const { graphic: rect } = chart?.selectedBackground ?? {};
              if (rect) {
                rect.destroy();
              }
              chart.selectedBackground = undefined;
            },
          },
        },
      },
      area: {
        linecap: 'square',
        stacking: getStackingFromPosition(position),
        marker: {
          enabled: true,
          radius: 0,
          symbol: 'circle',
        },
      },
    },
  };
};

const Area = (props: AreaProps) => {
  const { id, registerChild, isLoading } = useFlareContext();

  useEffect(() => {
    registerChild(id, (options: Options, parentProps: SanitizedFlareProps) =>
      buildAreaOptions(options, parentProps, props),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  if (isLoading) {
    return <AreaLoader style={{ flex: 1 }} />;
  }

  return null;
};

export default Area;
